import {
  Component,
  output,
  model,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { OutlookIqBoostStore } from '@maximizer/outlook/ai/data-access';

@Component({
  selector: 'maximizer-outlook-iq-boost-prompt-input',
  template: `
    @if (showPromptRequired() && !disabled()) {
      <p class="text-caption text-neutral-60 px-4 py-2">
        {{ 'outlook.iqBoost.promptRequired' | translate }}
      </p>
    }
    @if (showPromptTooShort() && !disabled()) {
      <p class="text-caption text-neutral-60 px-4 py-2">
        {{ 'outlook.iqBoost.promptTooShort' | translate }}
      </p>
    }

    <div class="bg-neutral-5 p-4">
      <kendo-textbox
        class="w-full"
        [disabled]="disabled()"
        [ngModel]="promptText()"
        (ngModelChange)="onPromptTextChange($event)"
        (keydown.enter)="onEnter()"
        [placeholder]="'outlook.iqBoost.askPlaceholder' | translate"
      >
        <ng-template kendoTextBoxSuffixTemplate>
          <button
            kendoButton
            themeColor="primary"
            [disabled]="disabled()"
            (click)="onSubmit()"
            iconClass="fa-icons icon-arrow-up icon-16"
            class="h-8 w-8 rounded-none"
            [title]="'outlook.iqBoost.askTitle' | translate"
            [attr.aria-label]="'outlook.iqBoost.askTitle' | translate"
          ></button>
        </ng-template>
      </kendo-textbox>
    </div>
  `,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    KENDO_BUTTON,
    KENDO_INPUTS,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class OutlookIqBoostPromptInputComponent {
  iqBoostStore = inject(OutlookIqBoostStore);

  promptText = model('');
  promptSubmit = output<string>();
  showPromptRequired = signal(false);
  showPromptTooShort = signal(false);

  disabled = computed(
    () =>
      this.iqBoostStore.loading() ||
      (!this.iqBoostStore.currentPromptSelection() &&
        this.iqBoostStore.promptCategories().length === 0),
  );

  onSubmit(): void {
    const text = this.promptText().trim();
    if (!text) {
      this.showPromptRequired.set(true);
      this.showPromptTooShort.set(false);
      return;
    }

    const wordCount = text.split(/\s+/).filter((w) => w.length > 0).length;
    if (wordCount < 2) {
      this.showPromptTooShort.set(true);
      this.showPromptRequired.set(false);
      return;
    }

    this.promptSubmit.emit(text);
    this.promptText.set('');
    this.showPromptRequired.set(false);
    this.showPromptTooShort.set(false);
  }

  onPromptTextChange(value: string): void {
    this.promptText.set(value);

    const trimmed = value.trim();
    if (this.showPromptRequired() && trimmed) {
      this.showPromptRequired.set(false);
    }
    if (
      this.showPromptTooShort() &&
      trimmed.split(/\s+/).filter((w) => w.length > 0).length >= 2
    ) {
      this.showPromptTooShort.set(false);
    }
  }

  onEnter(): void {
    this.onSubmit();
  }
}
